import React from 'react';
import styled from 'styled-components';
import { ChevronRight } from 'lucide-react';

// Styled Components – $tabs: horizontal tab bar on all screen sizes (e.g. settings modals)
const NavContainer = styled.nav<{ $tabs?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$tabs ? 'row' : 'column')};
  gap: ${(p) => (p.$tabs ? '8px' : '4px')};
  ${(p) =>
    p.$tabs &&
    `
  overflow-x: auto;
  padding: 8px 0;
  scrollbar-width: thin;
  scrollbar-color: ${p.theme.muted} transparent;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${p.theme.muted}; border-radius: 2px; }
  `}

  @media (max-width: 767px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px 0;
    gap: 8px;
    min-width: min-content;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: ${(p) => p.theme.muted} transparent;
    &::-webkit-scrollbar { height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: ${(p) => p.theme.muted}; border-radius: 2px; }
  }
`;

const NavItem = styled.button<{ $active: boolean; $tabs?: boolean }>`
  width: ${(p) => (p.$tabs ? 'auto' : '100%')};
  min-width: ${(p) => (p.$tabs ? 'fit-content' : undefined)};
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${(p) => (p.$tabs ? '8px 16px' : '10px 12px')};
  border-radius: ${(p) => (p.$tabs ? '20px' : '12px')};
  border: none;
  outline: none;
  box-shadow: none;
  background: ${(p) => (p.$active ? p.theme.muted : 'transparent')};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  position: relative;
  text-align: left;
  flex-shrink: 0;
  white-space: ${(p) => (p.$tabs ? 'nowrap' : undefined)};

  font-size: 14px;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  color: ${(p) => (p.$active ? p.theme.foreground : p.theme.mutedForeground)};

  &:hover {
    background: ${(p) => p.theme.muted};
    color: ${(p) => p.theme.foreground};
  }
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }

  ${(p) =>
    p.$tabs &&
    p.$active &&
    `
    background: ${p.theme.muted};
    color: ${p.theme.foreground};
  `}

  @media (max-width: 767px) {
    width: auto;
    min-width: fit-content;
    padding: 8px 16px;
    border-radius: 20px;
    white-space: nowrap;
    ${(p) =>
      p.$active &&
      `
      background: ${p.theme.muted};
      color: ${p.theme.foreground};
    `}
  }
`;

const NavIcon = styled.div<{ $tabs?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => (p.$tabs ? '16px' : '18px')};
  height: ${(p) => (p.$tabs ? '16px' : '18px')};
  opacity: 0.9;
  @media (max-width: 767px) {
    width: 16px;
    height: 16px;
  }
`;

const NavLabel = styled.span<{ $tabs?: boolean }>`
  flex: ${(p) => (p.$tabs ? 'none' : '1')};
  @media (max-width: 767px) {
    flex: none;
  }
`;

const NavChevron = styled.div<{ $active: boolean; $tabs?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  opacity: ${(p) => (p.$active ? 0.5 : 0)};
  transition: opacity 0.2s ease;
  ${(p) => p.$tabs && 'display: none;'}
  @media (max-width: 767px) {
    display: none;
  }
`;

// Types
interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
}

interface SidebarNavProps {
  items: NavItem[];
  activeItem: string;
  onItemClick: (itemId: string) => void;
  /** When true, show as horizontal tab bar on all screen sizes (e.g. landlord/tenant settings modal) */
  variant?: 'sidebar' | 'tabs';
}

// Component
export const SidebarNav: React.FC<SidebarNavProps> = ({ items, activeItem, onItemClick, variant = 'sidebar' }) => {
  const isTabs = variant === 'tabs';
  return (
    <NavContainer role="navigation" aria-label="Settings sections" $tabs={isTabs}>
      {items.map(({ id, label, icon: Icon }) => {
        const isActive = id === activeItem;
        
        return (
          <NavItem
            key={id}
            $active={isActive}
            $tabs={isTabs}
            onClick={() => onItemClick(id)}
            aria-current={isActive ? 'page' : undefined}
          >
            <NavIcon $tabs={isTabs}>
              <Icon size={isTabs ? 16 : 18} />
            </NavIcon>
            <NavLabel $tabs={isTabs}>{label}</NavLabel>
            <NavChevron $active={isActive} $tabs={isTabs}>
              <ChevronRight size={16} />
            </NavChevron>
          </NavItem>
        );
      })}
    </NavContainer>
  );
};